/**
 * UserNameWithBadges — single chokepoint for rendering a coach's name with
 * the right combination of badges (BAM Coach pill, BAM Cert seal, etc.).
 *
 * Drop this in anywhere a coach name appears (community feed, leaderboard,
 * post authors, drill bylines, profile headers, map markers). Centralizing
 * here means future badges (e.g. "BAM Partner Org member") only need to
 * be wired in one place.
 *
 * Priority rules:
 *   - BamCoachBadge is mutually exclusive with CertBadge. Internal staff
 *     don't show as "certified" — that's assumed.
 *   - When neither flag is set, only the plain name is rendered.
 *
 * Usage:
 *   <UserNameWithBadges
 *     profile={memberProfile}
 *     size="sm"             // or "md"
 *     style={{ color: C.text, fontWeight: 700 }}
 *   />
 *
 * profile expects (from `profiles` table):
 *   first_name, last_name (or display_name), is_bam_coach, cert_status
 */
import React from "react";
import { CertBadge } from "./CertBadge";
import { BamCoachBadge } from "./BamCoachBadge";
import { InternBadge } from "./InternBadge";

export function UserNameWithBadges({
  profile,
  size = "sm",
  showBadges = true,
  fallback = "Coach",
  style,
  badgeStyle,
  className,
}) {
  if (!profile) return <span style={style} className={className}>{fallback}</span>;

  const displayName =
    profile.display_name?.trim() ||
    [profile.first_name, profile.last_name].filter(Boolean).join(" ").trim() ||
    fallback;

  // BAM Coach takes precedence — staff don't double-up with cert badge.
  const showBam  = !!profile.is_bam_coach;
  const showCert =
    !showBam &&
    (profile.cert_status === "in_progress" || profile.cert_status === "certified");

  const gap = size === "md" ? 8 : 5;

  return (
    <span
      className={className}
      style={{ display: "inline-flex", alignItems: "center", gap, ...style }}
    >
      <span>{displayName}</span>
      {showBadges && showBam  && <BamCoachBadge size={size} style={badgeStyle} />}
      {showBadges && showCert && <CertBadge status={profile.cert_status} size={size} style={badgeStyle} />}
      {showBadges && <InternBadge userId={profile.id || profile.user_id} size={size} style={badgeStyle} />}
    </span>
  );
}

export default UserNameWithBadges;
